import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Play, MapPinned, Camera, CheckCircle2, Pencil, Loader2, Receipt, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { getLocation, SERVICE_LONG, TRIP_STATUS, PAY_STATUS, PAY_METHOD, fmtDateTime, filsToInput, fmtChanges } from "@/lib/util";
import { BackBar, Loader, Money, Pill, Row, Thumb, LocLink, Section, VehNo, tripColor, payColor } from "@/components/common";
import Modal from "@/components/Modal";
import PaymentPicker from "@/components/PaymentPicker";
import PhotoPicker from "@/components/PhotoPicker";
import PdfActions from "@/components/PdfActions";
import { uploadPhotos } from "@/pages/driver/NewTrip";
import CarPlateInput from "@/components/CarPlateInput";
import { tr as tx } from "@/lib/i18n";

function useAction(reload) {
  const [busy, setBusy] = useState(false);
  const run = async (fn, ok) => {
    setBusy(true);
    try {
      const loc = await getLocation();
      await fn(loc);
      if (ok) toast.success(ok);
      await reload();
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    } finally {
      setBusy(false);
    }
  };
  return [busy, run];
}

function DriverActions({ t, reload }) {
  const [busy, run] = useAction(reload);
  const [modal, setModal] = useState("");
  const [dest, setDest] = useState("");
  const [photos, setPhotos] = useState([]);
  const [pay, setPay] = useState({ payment_status: t.payment_status, paid_amount: filsToInput(t.paid_fils), payment_method: t.payment_method });
  const [edit, setEdit] = useState({});
  const close = () => setModal("");
  const openEdit = () => {
    setEdit({ requester_phone: t.requester_phone, requester_name: t.requester_name, car_plate: t.car_plate, load_desc: t.load_desc,
      pickup: t.pickup, price: filsToInput(t.price_fils), payment_status: t.payment_status, paid_amount: filsToInput(t.paid_fils), payment_method: t.payment_method });
    setModal("edit");
  };
  const Spin = busy ? <Loader2 className="w-6 h-6 animate-spin" /> : null;

  return (
    <div className="space-y-3">
      {t.status === "created" && (
        <button data-testid="btn-start-trip" disabled={busy} onClick={() => run((loc) => api.post(`/trips/${t.id}/start`, { loc }), tx("تم بدء النقل"))}
          className="btn btn-lg btn-blue w-full">{Spin || <Play className="w-7 h-7" />}{tx("بدء النقل")}</button>
      )}
      <div className="grid grid-cols-3 gap-2">
        <button data-testid="btn-change-destination" onClick={() => { setDest(""); setModal("dest"); }} className="btn btn-ghost h-20 flex-col text-base"><MapPinned className="w-7 h-7 text-blue-700" />{tx("تغيير الوجهة")}</button>
        <button data-testid="btn-add-photos" onClick={() => { setPhotos([]); setModal("photos"); }} className="btn btn-ghost h-20 flex-col text-base"><Camera className="w-7 h-7 text-blue-700" />{tx("إضافة صور")}</button>
        <button data-testid="btn-edit-trip" onClick={openEdit} className="btn btn-ghost h-20 flex-col text-base"><Pencil className="w-7 h-7 text-blue-700" />{tx("تعديل")}</button>
      </div>
      <button data-testid="btn-complete-trip" onClick={() => setModal("deliver")} className="btn btn-lg btn-green w-full"><CheckCircle2 className="w-7 h-7" />{tx("تم التسليم")}</button>

      <Modal open={modal === "dest"} onClose={close} title={tx("تغيير وجهة التسليم")} testid="dest-modal">
        <p className="text-slate-500">{tx("الوجهة الحالية:")} <b className="text-slate-900">{t.dropoff}</b></p>
        <input data-testid="new-destination-input" className="field" placeholder={tx("الوجهة الجديدة")} value={dest} onChange={(e) => setDest(e.target.value)} />
        <button data-testid="save-destination-btn" disabled={busy || !dest.trim()} className="btn btn-lg btn-blue w-full"
          onClick={async () => (await run((loc) => api.post(`/trips/${t.id}/destination`, { new_dropoff: dest, loc }), tx("تم تغيير الوجهة"))) && close()}>{Spin}{tx("حفظ الوجهة الجديدة")}</button>
      </Modal>

      <Modal open={modal === "photos"} onClose={close} title={tx("إضافة صور")} testid="photos-modal">
        <PhotoPicker files={photos} onChange={setPhotos} testid="extra-photos" />
        <button data-testid="upload-photos-btn" disabled={busy || !photos.length} className="btn btn-lg btn-blue w-full"
          onClick={async () => (await run((loc) => uploadPhotos(t.id, photos, t.status === "created" ? "before" : "during", loc), tx("تم رفع الصور"))) && close()}>{Spin}{tx("رفع الصور")}</button>
      </Modal>

      <Modal open={modal === "edit"} onClose={close} title={tx("تعديل بيانات النقلة")} testid="edit-modal">
        <div className="space-y-3">
          {t.service_type === "tow" && <label className="block"><span className="font-bold block mb-1">{tx("لوحة السيارة (الرقم الإداري / رقم اللوحة)")}</span>
            <CarPlateInput testid="edit-car_plate" value={edit.car_plate} onChange={(v) => setEdit({ ...edit, car_plate: v })} /></label>}
          {[["requester_phone", "هاتف طالب النقل"], ["requester_name", "اسم طالب النقل"], ...(t.service_type === "tow" ? [] : [["load_desc", "وصف الحمولة"]]), ["pickup", "موقع التحميل"], ["price", "السعر (د.ك)"]].map(([k, l]) => (
            <label key={k} className="block"><span className="font-bold block mb-1">{tx(l)}</span>
              <input data-testid={`edit-${k}`} className="field" value={edit[k] || ""} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} /></label>
          ))}
          <PaymentPicker value={edit} onChange={(v) => setEdit({ ...edit, ...v })} />
        </div>
        <button data-testid="save-edit-btn" disabled={busy} className="btn btn-lg btn-blue w-full"
          onClick={async () => (await run((loc) => api.patch(`/trips/${t.id}`, { ...edit, loc }), tx("تم حفظ التعديل"))) && close()}>{Spin}{tx("حفظ التعديل")}</button>
      </Modal>

      <Modal open={modal === "deliver"} onClose={close} title={tx("تأكيد التسليم")} testid="deliver-modal">
        <div className="rounded-xl bg-slate-50 p-3 flex justify-between"><span>{tx("سعر النقلة")}</span><Money fils={t.price_fils} /></div>
        <PaymentPicker value={pay} onChange={setPay} />
        <button data-testid="confirm-deliver-btn" disabled={busy} className="btn btn-lg btn-green w-full"
          onClick={async () => (await run((loc) => api.post(`/trips/${t.id}/deliver`, { ...pay, loc }), tx("تم التسليم وإصدار الفاتورة"))) && close()}>
          {Spin || <CheckCircle2 className="w-7 h-7" />}{tx("تأكيد: تم التسليم")}</button>
      </Modal>
    </div>
  );
}

function InvoiceBox({ inv, admin }) {
  return (
    <Section title={<span className="flex items-center gap-2"><Receipt className="w-6 h-6 text-orange-600" />{tx("الفاتورة")} <span className="ltr">{inv.number}</span></span>}
      action={admin && <Link data-testid="open-invoice-admin" to={`/admin/invoices/${inv.id}`} className="btn btn-sm btn-ghost">إدارة الفاتورة</Link>}>
      <Row label={tx("المبلغ")}><Money fils={inv.total_fils} /></Row>
      <Row label={tx("المحصل")}><Money fils={inv.paid_fils} /></Row>
      <Row label={tx("المتبقي")}><Money fils={inv.total_fils - inv.paid_fils} testid="invoice-remaining" /></Row>
      <Row label={tx("حالة الدفع")}><Pill color={payColor(inv.payment_status)} testid="invoice-pay-status">{tx(PAY_STATUS[inv.payment_status])}</Pill></Row>
      <div className="mt-4 space-y-3">
        <div className="font-bold text-slate-600">{tx("نسخة العميل")}</div>
        <PdfActions path={`/invoices/${inv.id}/pdf?copy=customer`} filename={`${inv.number}.pdf`} testid="invoice-pdf" />
        {admin && (<>
          <div className="font-bold text-slate-600 pt-2">النسخة الداخلية للشركة</div>
          <PdfActions path={`/invoices/${inv.id}/pdf?copy=internal`} filename={`${inv.number}-internal.pdf`} label="النسخة الداخلية" testid="invoice-internal-pdf" />
        </>)}
      </div>
    </Section>
  );
}

export default function TripView({ admin }) {
  const { id } = useParams();
  const [t, setT] = useState(null);
  const reload = useCallback(async () => {
    try { setT((await api.get(`/trips/${id}`)).data); } catch (e) { toast.error(errMsg(e)); setT(false); }
  }, [id]);
  useEffect(() => { reload(); }, [reload]);

  const header = admin
    ? <div className="flex items-center gap-3 mb-6"><Link to="/admin/trips" className="btn btn-sm btn-ghost"><ArrowRight className="w-4 h-4" />النقلات</Link><h1 className="text-2xl font-black">نقلة <span className="ltr">{t?.number}</span></h1></div>
    : <BackBar title={t ? tx("نقلة {n}", { n: t.number }) : tx("النقلة")} to="/driver/trips" />;
  if (t === null) return <>{header}<Loader /></>;
  if (!t) return <>{header}<p className="p-6 text-center">{tx("النقلة غير موجودة")}</p></>;
  const tow = t.service_type === "tow";

  return (
    <div>
      {header}
      <div className={`${admin ? "grid lg:grid-cols-2 gap-5" : "p-4 space-y-4"}`} data-testid="trip-view">
        <div className="space-y-4">
          <div className="card p-5">
            <div className="flex items-center justify-between gap-2 mb-3">
              <span className="font-extrabold text-blue-800">{tx(SERVICE_LONG[t.service_type] || "")}</span>
              <Pill color={tripColor(t.status)} testid="trip-status">{tx(TRIP_STATUS[t.status] || "")}</Pill>
            </div>
            <Row label={tx("من")}>{t.pickup}</Row>
            <Row label={tx("إلى")} testid="trip-dropoff-value">{t.dropoff}</Row>
            <Row label={tx("هاتف الطالب")}><a className="ltr text-blue-700" href={`tel:${t.requester_phone}`}>{t.requester_phone}</a></Row>
            {t.requester_name && <Row label={tx("الاسم")}>{t.requester_name}</Row>}
            {tow ? <><Row label={tx("لوحة السيارة")}><span className="ltr">{t.car_plate}</span></Row><Row label={tx("نوع السيارة")}>{t.car_type ? tx(t.car_type) : "—"}</Row></>
              : <><Row label={tx("نوع الأغراض")}>{t.goods_type ? tx(t.goods_type) : "—"}</Row><Row label={tx("الحمولة")}>{t.load_desc || "—"}</Row></>}
            <Row label={tx("السعر")}><Money fils={t.price_fils} testid="trip-price-value" /></Row>
            <Row label={tx("الدفع")}><span className="flex gap-2 items-center"><Pill color={payColor(t.payment_status)}>{tx(PAY_STATUS[t.payment_status] || "")}</Pill>{tx(PAY_METHOD[t.payment_method] || "")}</span></Row>
            <Row label={tx("تاريخ الإنشاء")}>{fmtDateTime(t.created_at)}</Row>
            {t.delivered_at && <Row label={tx("وقت التسليم")}>{fmtDateTime(t.delivered_at)}</Row>}
            {admin && <><Row label="السائق"><span>{t.driver_name} <span className="ltr text-slate-500">{t.driver_serial}</span></span></Row>
              <Row label="شاحنة النقل"><VehNo adm={t.vehicle_admin_number} plate={t.vehicle_plate} /></Row>
              <Row label="موقع الإنشاء"><LocLink loc={t.created_location} /></Row>
              {t.delivered_location && <Row label="موقع التسليم"><LocLink loc={t.delivered_location} /></Row>}</>}
          </div>
          {!admin && t.status !== "delivered" && <DriverActions t={t} reload={reload} />}
          {t.invoice && <InvoiceBox inv={t.invoice} admin={admin} />}
        </div>
        <div className="space-y-4">
          {t.photos.length > 0 && (
            <Section title={tx("الصور ({n})", { n: t.photos.length })}>
              <div className="grid grid-cols-3 gap-2">{t.photos.map((p) => <Thumb key={p.id} fileId={p.file_id} testid={`trip-photo-${p.id}`} />)}</div>
            </Section>
          )}
          {t.destinations.length > 0 && (
            <Section title={tx("سجل تغيير الوجهة")}>
              <div className="space-y-3" data-testid="destination-history">
                {t.destinations.map((d) => (
                  <div key={d.id} className="rounded-xl bg-blue-50 p-3">
                    <div className="font-bold"><s className="text-slate-400">{d.old}</s> ← {d.new}</div>
                    <div className="text-sm text-slate-600">{fmtDateTime(d.at)} • {d.by_name}</div>
                    {admin && <LocLink loc={d.location} />}
                  </div>
                ))}
              </div>
            </Section>
          )}
          {admin && t.audit && (
            <Section title="سجل الإجراءات والمواقع">
              <div className="space-y-2 text-sm" data-testid="trip-audit">
                {t.audit.map((a) => (
                  <div key={a.id} className="border-b border-slate-100 pb-2">
                    <div className="flex justify-between gap-2"><b>{a.action}</b><span className="text-slate-500">{fmtDateTime(a.at)}</span></div>
                    <div className="text-slate-600">{a.user_name} {a.location && <LocLink loc={a.location} />}</div>
                    {a.before && Object.keys(a.before).length > 0 && <div className="text-xs text-slate-500">قبل: {fmtChanges(a.before)} ← بعد: {fmtChanges(a.after)}</div>}
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}
